import React from 'react';
import {
  Button,
  LockIcon,
  Theme,
  ThemeName,
  ToggleSwitch,
  UnlockIcon,
} from '@taopix/taopix-design-system';
import {t} from 'i18next';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import classNames from 'classnames';
import {ViewMode} from '@taopix/taopix-core';
import {useExperience} from '../Experience/ExperienceProvider';
import useViewMode from '../../Hooks/useViewMode';
import {useRegisterKeyboardCommand} from '../Context/KeyboardCommandsContext';
import {useTranslation} from 'react-i18next';

const LayoutLockSwitch = () => {
  const ui = useUiState();
  const {experience} = useExperience();
  const [viewMode] = useViewMode();
  const {t} = useTranslation(['*']);

  useRegisterKeyboardCommand({
    code: 'Digit5',
    shiftKey: true,
    callback: () => {
      // toggle switch
      onChangeCanEditPageLayout(!ui.state.canEditPageLayout);
    },
  });

  const onChangeCanEditPageLayout = (truthy: boolean): void => {
    // Delay the dispatch to the core to give the toggle switch time to update
    setTimeout(() => ui.dispatch(UserInterfaceActions.setCanEditPageLayout(truthy)), 500);
  };

  const containerClasses = classNames('flex', 'shadow-md', 'overflow-hidden');

  // Don't show the layout lock switch during zoom, or grid or preview views, or if it is disabled in the experience
  if (
    ui.state.zoomLevel > 100 ||
    [ViewMode.Grid, ViewMode.Preview].includes(viewMode) ||
    !experience.EDITOR.pageEditing.allowUsersToToggleLocking
  ) {
    return <></>;
  }

  return (
    <Theme name={ThemeName.Popouts} className={containerClasses}>
      {ui.state.isSmallScreen ? (
        <>
          <Button
            label={t('*:str_LabelUnlockLayout', {defaultValue: 'Unlock layout'})}
            hideLabel
            startIcon={<UnlockIcon />}
            buttonStyle={'standard'}
            aria-pressed={ui.state.canEditPageLayout}
            onClick={() => onChangeCanEditPageLayout(true)}
            corners={'square'}
          />
          <Button
            label={t('*:str_LabelLockLayout', {defaultValue: 'Lock layout'})}
            hideLabel
            startIcon={<LockIcon />}
            buttonStyle={'standard'}
            aria-pressed={!ui.state.canEditPageLayout}
            onClick={() => onChangeCanEditPageLayout(false)}
            corners={'square'}
          />
        </>
      ) : (
        <div className={'h-[30rem] flex items-center px-sm'}>
          <ToggleSwitch
            label={t('*:str_ButtonSwitchToAdvancedEditing')}
            onChange={isChecked => onChangeCanEditPageLayout(isChecked)}
            defaultChecked={ui.state.canEditPageLayout}
            id={'toggleLayoutLock'}
            labelPosition="left"
            labelAlignment="right"
          />
        </div>
      )}
    </Theme>
  );
};

export default LayoutLockSwitch;
